import React, { useState, useEffect, useRef } from 'react';
import { serviceOrderService } from '../../services/serviceOrderService';
import { supplierService } from '../../services/supplierService';
import SupplierSelector from '../PurchaseOrders/SupplierSelector';
import ServiceSelector from './ServiceSelector';
import CreateSupplierModal from '../PurchaseOrders/CreateSupplierModal';
import Toast from '../Common/Toast';
import PriceIgvHint from '../Common/PriceIgvHint';
import PricesIncludeIgvToggle from '../Common/PricesIncludeIgvToggle';
import UnitPriceTextInput from '../Common/UnitPriceTextInput';
import QuantityTextInput from '../Common/QuantityTextInput';
import { roundPrice } from '../../utils/priceIgvUtils';
import { useComprasPurchaseSiteId } from '../../utils/comprasPurchaseSite';

const ServiceOrderForm = ({ isOpen, onClose, order = null, onSuccess, isPageMode = false }) => {
  const purchaseSiteId = useComprasPurchaseSiteId();
  const [formData, setFormData] = useState({
    supplier_id: '',
    igv_rate: '18',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    payment_condition_type: 'cash', // 'cash', 'days' o 'date'
    payment_condition_days: 30, // Días para pagar (si type es 'days')
    payment_condition_date: '', // Fecha específica (si type es 'date')
    notes: '',
    items: [], // Array de { service_id, description, project_id, quantity, unit_price, has_igv }
  });

  const [suppliers, setSuppliers] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [codePreview, setCodePreview] = useState('');
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const [isEditingAmounts, setIsEditingAmounts] = useState(false);
  const [amountOverride, setAmountOverride] = useState(null);
  const [amountForm, setAmountForm] = useState({ igv_amount: '' });
  const hasLoadedFormData = useRef(false);
  const hasLoadedOrder = useRef(false);
  const [pricesIncludeIgv, setPricesIncludeIgv] = useState(false);

  // Cargar datos del formulario (una sola petición API)
  useEffect(() => {
    if ((isOpen || isPageMode) && !hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, [isOpen, isPageMode]);

  // Al cerrar el modal, resetear para que al reabrir se recarguen datos
  useEffect(() => {
    if (!isOpen && !isPageMode) {
      hasLoadedFormData.current = false;
    }
  }, [isOpen, isPageMode]);

  // Generar preview de código
  useEffect(() => {
    if (!order) {
      setCodePreview('OS-XXXX');
    }
  }, [order]);

  // Cargar orden si se pasa un ID (modo edición)
  useEffect(() => {
    if (order && typeof order === 'object' && order.id && !hasLoadedOrder.current) {
      console.log('ðŸ“ [ServiceOrderForm] Cargando datos de orden para edición:', order);
      loadOrderDataFromObject(order);
    }
  }, [order]);

  const loadFormData = async () => {
    try {
      const res = await serviceOrderService.getFormData();
      const d = res.data || {};
      if (res.success) {
        setSuppliers(d.suppliers || []);
        setServices(d.services || []);
        setProjects(d.projects || []);
      }
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    }
  };

  const handleSupplierCreated = async (newSupplier) => {
    // Recargar la lista completa de proveedores desde el backend
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error al recargar proveedores:', error);
      // Si falla, al menos agregar el nuevo proveedor localmente
      setSuppliers(prev => [...prev, newSupplier]);
    }
    
    // Seleccionar el nuevo proveedor
    setFormData(prev => ({
      ...prev,
      supplier_id: newSupplier.id.toString(),
    }));
    setToast({
      isOpen: true,
      message: 'Proveedor creado y seleccionado exitosamente',
      type: 'success'
    });
  };

  // Función auxiliar para convertir fecha ISO a formato YYYY-MM-DD
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return '';
    }
  };

  const loadOrderDataFromObject = (orderData) => {
    hasLoadedOrder.current = true;
    
    const formattedOrderDate = formatDateForInput(orderData.order_date);
    const formattedDeliveryDate = formatDateForInput(orderData.expected_delivery_date);
    const formattedPaymentDate = formatDateForInput(orderData.payment_condition_date);
    
    console.log('📅 [ServiceOrderForm] Formateando fechas:', {
      original_order_date: orderData.order_date,
      formatted_order_date: formattedOrderDate,
      original_expected_delivery_date: orderData.expected_delivery_date,
      formatted_expected_delivery_date: formattedDeliveryDate,
      original_payment_condition_date: orderData.payment_condition_date,
      formatted_payment_condition_date: formattedPaymentDate,
    });
    
    setFormData({
      supplier_id: orderData.supplier_id ? String(orderData.supplier_id) : '',
      igv_rate: String(orderData.items?.[0]?.igv_rate ?? 18),
      order_date: formattedOrderDate || new Date().toISOString().split('T')[0],
      expected_delivery_date: formattedDeliveryDate || '',
      payment_condition_type: orderData.payment_condition_type || 'cash',
      payment_condition_days: orderData.payment_condition_days || 30,
      payment_condition_date: formattedPaymentDate || '',
      notes: orderData.notes || '',
      items: orderData.items?.map(item => ({
        id: item.id,
        service_id: item.service_id,
        description: item.description || item.service?.name || '',
        project_id: item.project_id || item.project?.id || '',
        quantity: item.quantity,
        unit_price: item.unit_price,
        has_igv: item.has_igv ?? true, // Por defecto true
      })) || [],
    });

    if (orderData.code) {
      setCodePreview(orderData.code);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = 'El proveedor es requerido';
    }

    if (!formData.order_date) {
      newErrors.order_date = 'La fecha de orden es requerida';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Debe agregar al menos un item a la orden';
    }

    formData.items.forEach((item, index) => {
      if (!item.service_id) {
        newErrors[`items.${index}.service_id`] = 'El servicio es requerido';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`items.${index}.quantity`] = 'La cantidad debe ser mayor a 0';
      }
      if (!item.unit_price || item.unit_price <= 0) {
        newErrors[`items.${index}.unit_price`] = 'El precio unitario debe ser mayor a 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { service_id: '', description: '', project_id: '', quantity: 1, unit_price: 0, has_igv: true }],
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      const updatedItem = {
        ...currentItem,
        [field]: field === 'quantity'
          ? parseFloat(value) || 0
          : field === 'unit_price'
          ? roundPrice(parseFloat(value) || 0)
          : field === 'has_igv'
          ? value === 'true' || value === true
          : value,
      };

      // Si se cambia el servicio, actualizar automáticamente la descripción con el nombre del servicio
      // como valor por defecto (opcional, el usuario puede cambiarla)
      if (field === 'service_id' && value) {
        const service = services.find(s => s.id === parseInt(value));
        console.log('🔄 [ServiceOrderForm] Servicio seleccionado:', service);
        console.log('ðŸ“ [ServiceOrderForm] Unidad de medida del servicio:', service?.description);
        if (service && service.name) {
          // Actualizar con el nombre del servicio como descripción por defecto
          updatedItem.description = service.name;
          console.log('✅ [ServiceOrderForm] Descripción actualizada a:', service.name);
        } else {
          // Si el servicio no tiene nombre, limpiar el campo
          updatedItem.description = '';
          console.log('âš ï¸ [ServiceOrderForm] Servicio sin unidad de medida');
        }
      }

      newItems[index] = updatedItem;
      return {
        ...prev,
        items: newItems,
      };
    });
  };

  const calculateTotal = () => {
    const igvRatePercent = parseFloat(formData.igv_rate) || 18;
    const IGV_RATE = igvRatePercent / 100;
    
    let baseImponible = 0; // Items con IGV
    let noGravados = 0; // Items sin IGV
    
    formData.items.forEach(item => {
      const subtotal = item.quantity * item.unit_price;
      if (item.has_igv) {
        baseImponible += subtotal;
      } else {
        noGravados += subtotal;
      }
    });
    
    const igv = baseImponible * IGV_RATE;
    const total = baseImponible + igv + noGravados;
    
    return {
      baseImponible,
      igv,
      noGravados,
      total
    };
  };

  // Calcular fecha de pago basándose en la condición seleccionada
  const calculatePaymentDate = () => {
    if (!formData.order_date) return null;
    
    const orderDate = new Date(formData.order_date);
    
    if (formData.payment_condition_type === 'cash') {
      // Al contado: la fecha de pago es la misma que la fecha de orden
      return orderDate;
    } else if (formData.payment_condition_type === 'days') {
      const days = parseInt(formData.payment_condition_days) || 0;
      const paymentDate = new Date(orderDate);
      paymentDate.setDate(paymentDate.getDate() + days);
      return paymentDate;
    } else if (formData.payment_condition_type === 'date' && formData.payment_condition_date) {
      return new Date(formData.payment_condition_date);
    }
    
    return null;
  };

  // Calcular días hasta la fecha de pago
  const calculateDaysUntilPayment = () => {
    const paymentDate = calculatePaymentDate();
    if (!paymentDate || !formData.order_date) return null;
    
    const orderDate = new Date(formData.order_date);
    const diffTime = paymentDate - orderDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setToast({
        isOpen: true,
        message: 'Por favor, corrige los errores en el formulario',
        type: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const paymentDate = calculatePaymentDate();
      const calculated = calculateTotal();
      const totals = amountOverride || calculated;

      const submitData = {
        supplier_id: parseInt(formData.supplier_id),
        igv_rate: parseFloat(formData.igv_rate),
        order_date: formData.order_date,
        expected_delivery_date: formData.expected_delivery_date || null,
        payment_condition_type: formData.payment_condition_type,
        payment_condition_days: formData.payment_condition_type === 'days' ? parseInt(formData.payment_condition_days) : null,
        payment_condition_date: formData.payment_condition_type === 'date' ? formData.payment_condition_date : null,
        payment_date: paymentDate ? paymentDate.toISOString().split('T')[0] : null,
        notes: formData.notes,
        base_imponible: totals.baseImponible,
        igv_amount: totals.igv,
        no_gravados: totals.noGravados,
        total_amount: totals.total,
        items: formData.items.map(item => ({
          service_id: parseInt(item.service_id),
          description: item.description || null,
          project_id: item.project_id ? parseInt(item.project_id) : null,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          has_igv: item.has_igv ?? true,
        })),
        ...(purchaseSiteId && !order?.id ? { purchase_site_id: purchaseSiteId } : {}),
      };

      let response;
      if (order && order.id) {
        response = await serviceOrderService.updateServiceOrder(order.id, submitData);
        setToast({
          isOpen: true,
          message: 'Orden de servicio actualizada exitosamente',
          type: 'success'
        });
      } else {
        response = await serviceOrderService.createServiceOrder(submitData);
        setToast({
          isOpen: true,
          message: 'Orden de servicio creada exitosamente',
          type: 'success'
        });
      }

      if (response.success) {
        setTimeout(() => {
          onSuccess();
          if (!isPageMode) {
            onClose();
          }
        }, 1000);
      } else {
        throw new Error(response.message || 'Error al guardar la orden de servicio');
      }
    } catch (error) {
      console.error('Error:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setToast({
          isOpen: true,
          message: 'Por favor, corrige los errores en el formulario',
          type: 'error'
        });
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Error al guardar la orden de servicio';
        setErrors({ submit: errorMessage });
        setToast({
          isOpen: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isPageMode) return null;

  const formContent = (
    <>
      <form onSubmit={handleSubmit} className={isPageMode ? "p-8" : "px-6 py-4"}>
        {errors.submit && (
          <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errors.submit}
          </div>
        )}

        <div className="space-y-6">
          {/* Sección: Código Preview - Solo mostrar en modo creación */}
          {!order && codePreview && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-5 shadow-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                Código (Generado automáticamente)
              </label>
              <div className="text-2xl font-mono font-bold text-teal-700 mb-1">{codePreview}</div>
              <p className="text-xs text-gray-600 mt-2">Este código se generará automáticamente al guardar la orden</p>
            </div>
          )}

          {/* Sección: Información Básica y Condición de Pago - Lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sección: Información Básica */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Información Básica
              </h3>
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="supplier_id" className="block text-sm font-medium text-gray-700">
                      Proveedor <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsCreateSupplierModalOpen(true)}
                      className="text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                    >
                      + Nuevo
                    </button>
                  </div>
                  <SupplierSelector
                    value={formData.supplier_id}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        supplier_id: value.toString(),
                      }));
                      if (errors.supplier_id) {
                        setErrors(prev => ({
                          ...prev,
                          supplier_id: null,
                        }));
                      }
                    }}
                    error={errors.supplier_id}
                    suppliers={suppliers}
                  />
                </div>

                <div>
                  <label htmlFor="igv_rate" className="block text-sm font-medium text-gray-700 mb-2">
                    Tasa de IGV
                  </label>
                  <select
                    id="igv_rate"
                    name="igv_rate"
                    value={formData.igv_rate}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    <option value="18">18%</option>
                    <option value="10">10%</option>
                    <option value="10.5">10.5%</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="order_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Orden <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="order_date"
                    name="order_date"
                    value={formData.order_date}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.order_date ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                    }`}
                  />
                  {errors.order_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.order_date}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="expected_delivery_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Inicio de Servicio
                  </label>
                  <input
                    type="date"
                    id="expected_delivery_date"
                    name="expected_delivery_date"
                    value={formData.expected_delivery_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Sección: Condición de Pago */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Condición de Pago
            </h3>
            <div className="space-y-4">
              {/* Tipo de condición */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipo de Condición
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="cash"
                      checked={formData.payment_condition_type === 'cash'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Al contado</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="days"
                      checked={formData.payment_condition_type === 'days'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Cada X días (máx. 3 meses)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="date"
                      checked={formData.payment_condition_type === 'date'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Fecha específica</span>
                  </label>
                </div>
              </div>

              {/* Opción: Cada X días */}
              {formData.payment_condition_type === 'days' && (
                <div>
                  <label htmlFor="payment_condition_days" className="block text-sm font-medium text-gray-700 mb-2">
                    Días para pagar
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      id="payment_condition_days"
                      name="payment_condition_days"
                      value={formData.payment_condition_days}
                      onChange={(e) => {
                        const days = parseInt(e.target.value) || 0;
                        const maxDays = 90; // 3 meses máximo
                        const validDays = Math.min(Math.max(1, days), maxDays);
                        setFormData(prev => ({
                          ...prev,
                          payment_condition_days: validDays,
                        }));
                      }}
                      min="1"
                      max="90"
                      className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    />
                    <span className="text-sm text-gray-600">días (máximo 90 días / 3 meses)</span>
                  </div>
                </div>
              )}

              {/* Opción: Fecha específica */}
              {formData.payment_condition_type === 'date' && (
                <div>
                  <label htmlFor="payment_condition_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Pago
                  </label>
                  <input
                    type="date"
                    id="payment_condition_date"
                    name="payment_condition_date"
                    value={formData.payment_condition_date}
                    onChange={handleChange}
                    min={formData.order_date}
                    className="w-full md:w-64 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </div>
              )}

              {/* Información de fecha de pago y contador de días */}
              {calculatePaymentDate() && (
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Fecha de Pago Calculada</div>
                      <div className="text-lg font-semibold text-teal-700">
                        {calculatePaymentDate().toLocaleDateString('es-PE', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {formData.payment_condition_type === 'cash' && (
                          <span className="ml-2 text-sm text-teal-600 font-normal">(Al contado)</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Días hasta el pago</div>
                      <div className="text-2xl font-bold text-teal-600">
                        {calculateDaysUntilPayment() !== null ? (
                          <>
                            {calculateDaysUntilPayment()} 
                            <span className="text-base ml-1">
                              {calculateDaysUntilPayment() === 0 ? 'día (al contado)' : calculateDaysUntilPayment() === 1 ? 'día' : 'días'}
                            </span>
                          </>
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Sección: Items de la Orden */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col gap-3 mb-5 pb-3 border-b border-gray-200">
              <div className="flex flex-wrap justify-between items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Items de la Orden
                </h3>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                >
                  + Agregar Item
                </button>
              </div>
              <PricesIncludeIgvToggle
                value={pricesIncludeIgv}
                onChange={setPricesIncludeIgv}
                igvRate={formData.igv_rate}
              />
            </div>

            {errors.items && (
              <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {errors.items}
              </div>
            )}

            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No hay items agregados</p>
                <p className="text-sm mt-2">Haz clic en "Agregar Item" para comenzar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.items.map((item, index) => {
                  const service = services.find(s => s.id === parseInt(item.service_id));
                  const subtotal = item.quantity * item.unit_price;
                  
                  return (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex gap-2 items-end">
                        {/* Servicio */}
                        <div className="flex-1 min-w-[160px] max-w-[200px]">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Servicio <span className="text-red-500">*</span>
                          </label>
                          <ServiceSelector
                            value={item.service_id}
                            onChange={(value) => handleItemChange(index, 'service_id', value)}
                            services={services}
                            error={errors[`items.${index}.service_id`]}
                            compact={true}
                          />
                          {errors[`items.${index}.service_id`] && (
                            <p className="mt-0.5 text-xs text-red-600">{errors[`items.${index}.service_id`]}</p>
                          )}
                        </div>

                        {/* Descripción */}
                        <div className="flex-1 min-w-[140px] max-w-[180px]">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Descripción
                          </label>
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            placeholder="Descripción"
                          />
                        </div>

                        {/* Proyecto */}
                        <div className="flex-shrink-0 w-32">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Proyecto
                          </label>
                          <select
                            value={item.project_id || ''}
                            onChange={(e) => handleItemChange(index, 'project_id', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          >
                            <option value="">Seleccionar</option>
                            {projects.map((project) => (
                              <option key={project.id} value={project.id}>
                                {project.code} - {project.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Cantidad */}
                        <div className="flex-shrink-0 w-16">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Cant. <span className="text-red-500">*</span>
                          </label>
                          <QuantityTextInput
                            value={item.quantity}
                            onChange={(qty) => handleItemChange(index, 'quantity', qty)}
                            min={0.001}
                            className={`w-full px-1.5 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 transition-all ${
                              errors[`items.${index}.quantity`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                            }`}
                            placeholder="0"
                          />
                          {errors[`items.${index}.quantity`] && (
                            <p className="mt-0.5 text-xs text-red-600">{errors[`items.${index}.quantity`]}</p>
                          )}
                        </div>

                        {/* Precio Unitario */}
                        <div className="flex-shrink-0 w-24">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            P.Unit. <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="absolute left-1.5 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">S/</span>
                            <UnitPriceTextInput
                              netPrice={item.unit_price}
                              onNetPriceChange={(net) => handleItemChange(index, 'unit_price', net)}
                              pricesIncludeIgv={pricesIncludeIgv}
                              hasIgv={item.has_igv ?? true}
                              igvRate={formData.igv_rate}
                              className={`w-full pl-5 pr-1.5 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-1 transition-all ${
                                errors[`items.${index}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                              }`}
                              placeholder="0.0000"
                            />
                          </div>
                          <div className="min-h-[14px] mt-0.5 text-[10px] leading-[14px]">
                            <PriceIgvHint
                              netPrice={item.unit_price}
                              igvRate={formData.igv_rate}
                              hasIgv={item.has_igv ?? true}
                              pricesIncludeIgv={pricesIncludeIgv}
                            />
                          </div>
                          {errors[`items.${index}.unit_price`] && (
                            <p className="mt-0.5 text-xs text-red-600">{errors[`items.${index}.unit_price`]}</p>
                          )}
                        </div>

                        {/* ¿Tiene IGV? */}
                        <div className="flex-shrink-0 w-16">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            IGV
                          </label>
                          <select
                            value={item.has_igv ? 'true' : 'false'}
                            onChange={(e) => handleItemChange(index, 'has_igv', e.target.value)}
                            className="w-full px-1.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                          >
                            <option value="true">Sí</option>
                            <option value="false">No</option>
                          </select>
                        </div>

                        {/* Subtotal */}
                        <div className="flex-shrink-0 w-24">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Subtotal
                          </label>
                          <div className="px-2 py-1.5 text-xs bg-gray-100 border border-gray-300 rounded-md text-gray-700 font-medium">
                            S/ {parseFloat(subtotal).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>

                        {/* Botón Eliminar */}
                        <div className="flex-shrink-0 w-10 flex items-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="w-full px-2 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                            title="Eliminar item"
                          >
                            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Totales */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-full md:w-80 space-y-2">
                  {(() => {
                    const calculated = calculateTotal();
                    const totals = amountOverride || calculated;
                    const base = totals.baseImponible;
                    const noGrav = totals.noGravados;
                    if (!isEditingAmounts) {
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Base Imponible:</span>
                            <span className="font-medium text-gray-700">S/ {parseFloat(base).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">IGV ({formData.igv_rate}%):</span>
                            <span className="font-medium text-gray-700">S/ {parseFloat(totals.igv).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">No Gravados:</span>
                            <span className="font-medium text-gray-700">S/ {parseFloat(noGrav).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-300">
                            <span className="text-base font-semibold text-gray-800">Total:</span>
                            <span className="text-2xl font-bold text-teal-600">S/ {parseFloat(totals.total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="pt-2">
                            <button type="button" onClick={() => { setAmountForm({ igv_amount: String(totals.igv) }); setIsEditingAmounts(true); }} className="text-sm text-teal-600 hover:text-teal-700 font-medium" title="Ajustar IGV por diferencias de decimales">Ajustar montos</button>
                          </div>
                        </>
                      );
                    }
                    const igvNum = parseFloat(amountForm.igv_amount) || 0;
                    const totalRecalc = Math.round((base + igvNum + noGrav) * 100) / 100;
                    return (
                      <div className="pt-2 border-t border-gray-300 space-y-3">
                        <p className="text-xs text-gray-500">Modifique el IGV para corregir la diferencia por decimales. El Total se recalcula automáticamente.</p>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">IGV</label>
                          <input type="number" step="0.01" min="0" value={amountForm.igv_amount} onChange={(e) => setAmountForm({ igv_amount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Total (recalculado)</label>
                          <p className="text-sm font-semibold text-teal-600 py-1.5">S/ {totalRecalc.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => { setAmountOverride({ baseImponible: base, igv: igvNum, noGravados: noGrav, total: totalRecalc }); setIsEditingAmounts(false); }} className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-md">Guardar</button>
                          <button type="button" onClick={() => setIsEditingAmounts(false)} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded-md">Cancelar</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Sección: Observaciones */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Observaciones
            </h3>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              placeholder="Agregar notas u observaciones sobre la orden..."
            />
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={isPageMode ? () => window.history.back() : onClose}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (order ? 'Actualizar' : 'Crear Orden')}
            </button>
          </div>
        </div>
      </form>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      <CreateSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreated}
        forceProviderOnly={true}
      />
    </>
  );

  if (isPageMode) {
    return formContent;
  }

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'block' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-teal-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {order ? 'Editar Orden de Servicio' : 'Nueva Orden de Servicio'}
              </h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {formContent}
        </div>
      </div>
    </div>
  );
};

export default ServiceOrderForm;

